import type {
  ConnectWorkflow,
  ConnectWorkflowEdge,
  ConnectWorkflowNode,
} from '../../../services/connect/connect-workflows.service';
import {
  ConnectNodeFieldShape,
  ConnectWorkflowNodePaletteItem,
  isConnectGuidedBindingToken,
  isConnectGuidedCurrentItemBindingToken,
  isConnectGuidedRuntimeLikeString,
  isConnectNodeOutputCompatible,
  parseConnectGuidedPriorStepBindingToken,
  readConnectNodeOperationInput,
  resolveConnectNodeOperation,
} from '../../../services/connect/connect-nodes.service';

export type ConnectGuidedActionBlock =
  | {kind: 'prepare-information'}
  | {kind: 'verify-local-account'}
  | ConnectGuidedConnectorOperationBlock;

export type ConnectGuidedConnectorOperationBlock = {
  kind: 'connector-operation';
  operationRef: string;
  connectionSlot: string;
  connectionProviderRef: string;
  input: Record<string, unknown>;
};

export type ConnectGuidedForEachBlock = {
  kind: 'for-each-item';
  body: ConnectGuidedBlock[];
};

export type ConnectGuidedDecisionCondition = {
  field: 'priority' | 'subject' | 'details';
  operator: 'equals' | 'not-equals';
  value: string;
};

export type ConnectGuidedPriorityDecisionBlock = {
  kind: 'priority-decision';
  condition?: ConnectGuidedDecisionCondition;
  urgent: {blocks: ConnectGuidedBlock[]; outcome: string};
  standard: {blocks: ConnectGuidedBlock[]; outcome: string};
};

export type ConnectGuidedBlock =
  | ConnectGuidedActionBlock
  | ConnectGuidedForEachBlock
  | ConnectGuidedPriorityDecisionBlock;

export type ConnectGuidedWorkflowDraft = {
  name: string;
  urgentOutcome: string;
  standardOutcome: string;
  account?: 'none' | 'local-preview';
};

export type ConnectGuidedWorkflowOutline = {
  name: string;
  blocks: ConnectGuidedBlock[];
};

const PROVIDER_FREE_TAGS = ['guided', 'provider-free', 'local-demo'];
const LOCAL_PREVIEW_TAGS = ['guided', 'local-demo', 'connected-account'];
const DEFAULT_DECISION_CONDITION: ConnectGuidedDecisionCondition = {
  field: 'priority',
  operator: 'equals',
  value: 'Urgent',
};
const DECISION_FIELDS = ['priority', 'subject', 'details'] as const;
const DECISION_OPERATORS = ['equals', 'not-equals'] as const;
const MAX_CONDITION_VALUE_LENGTH = 120;
const MAX_BLOCKS_PER_SECTION = 10;

type CompiledAction = {
  entryId: string;
  exitId: string;
  nodes: ConnectWorkflowNode[];
  edges: ConnectWorkflowEdge[];
  connections: ConnectGuidedConnectionDeclaration[];
};

type ConnectGuidedConnectionDeclaration = {name: string; ref: string};

export type ConnectGuidedConnectorProjector = (
  node: ConnectWorkflowNode,
  connectionProviderRef: string,
) => ConnectGuidedConnectorOperationBlock | null;

export type ConnectGuidedNodeRole =
  | 'start'
  | 'action'
  | 'decision'
  | 'urgent-outcome'
  | 'standard-outcome';

export type ConnectGuidedNodeIdentityResolver = (
  block: ConnectGuidedBlock | null,
  role: ConnectGuidedNodeRole,
  suggestedId: string,
) => string;

export type ConnectGuidedCompilationTrace = {
  workflow: ConnectWorkflow;
  triggerNodeId: string;
  blockNodeIds: Map<ConnectGuidedBlock, string>;
  decisionOutcomeIds: Map<
    ConnectGuidedPriorityDecisionBlock,
    {urgent: string; standard: string}
  >;
};

export function compileGuidedWorkflow(
  draft: ConnectGuidedWorkflowDraft,
  catalog?: readonly ConnectWorkflowNodePaletteItem[],
): ConnectWorkflow;
export function compileGuidedWorkflow(
  draft: ConnectGuidedWorkflowOutline,
  catalog?: readonly ConnectWorkflowNodePaletteItem[],
): ConnectWorkflow;
export function compileGuidedWorkflow(
  input: ConnectGuidedWorkflowDraft | ConnectGuidedWorkflowOutline,
  catalog?: readonly ConnectWorkflowNodePaletteItem[],
): ConnectWorkflow {
  return compileGuidedWorkflowWithTrace(input, undefined, catalog).workflow;
}

export function compileGuidedWorkflowWithTrace(
  input: ConnectGuidedWorkflowDraft | ConnectGuidedWorkflowOutline,
  resolveNodeId?: ConnectGuidedNodeIdentityResolver,
  catalog?: readonly ConnectWorkflowNodePaletteItem[],
): ConnectGuidedCompilationTrace {
  const draft = isOutline(input) ? input : upgradeLegacyDraft(input);
  validateDraft(draft);
  const decision = finalDecision(draft.blocks);
  const triggerNodeId = resolveIdentity(resolveNodeId, null, 'start', 'start');
  const blockNodeIds = new Map<ConnectGuidedBlock, string>();
  const decisionOutcomeIds = new Map<
    ConnectGuidedPriorityDecisionBlock,
    {urgent: string; standard: string}
  >();
  const nodes: ConnectWorkflowNode[] = [{
    id: triggerNodeId,
    type: 'trigger.manual',
    ref: 'system.trigger.manual',
  }];
  const edges: ConnectWorkflowEdge[] = [];
  const connections = new Map<string, string>();

  const appendActions = (
    actions: Array<ConnectGuidedActionBlock | ConnectGuidedForEachBlock>,
    prefix: string,
    sourceId: string,
    firstEdge?: Pick<ConnectWorkflowEdge, 'source_handle' | 'label'>,
  ): string => {
    let previousId = sourceId;
    const occurrences = new Map<string, number>();
    actions.forEach((action, index) => {
      const occurrenceKey = actionOccurrenceKey(action);
      const occurrence = (occurrences.get(occurrenceKey) ?? 0) + 1;
      occurrences.set(occurrenceKey, occurrence);
      const compiled = compileAction(
        action,
        prefix,
        false,
        occurrence,
        resolveNodeId,
        blockNodeIds,
      );
      nodes.push(...compiled.nodes);
      edges.push({
        id: `${previousId}-${compiled.entryId}`,
        source: previousId,
        target: compiled.entryId,
        ...(index === 0 ? firstEdge : {}),
      }, ...compiled.edges);
      previousId = compiled.exitId;
      compiled.connections.forEach(connection => {
        const current = connections.get(connection.name);
        if (current && current !== connection.ref) {
          throw new Error('Workflow connection authority is ambiguous.');
        }
        connections.set(connection.name, connection.ref);
      });
    });
    return previousId;
  };

  const beforeDecision = appendActions(
    (decision ? draft.blocks.slice(0, -1) : draft.blocks) as Array<ConnectGuidedActionBlock | ConnectGuidedForEachBlock>,
    '',
    triggerNodeId,
  );
  if (!decision) {
    assertUniqueNodeIds(nodes);
    assertPriorStepBindings(draft, blockNodeIds, catalog);
    return {
      workflow: buildWorkflow(draft.name, nodes, edges, connections),
      triggerNodeId,
      blockNodeIds,
      decisionOutcomeIds,
    };
  }
  const decisionCondition = normalizeDecisionCondition(decision.condition);
  const decisionNodeId = resolveIdentity(
    resolveNodeId,
    decision,
    'decision',
    'priority-decision',
  );
  blockNodeIds.set(decision, decisionNodeId);
  nodes.push({
    id: decisionNodeId,
    type: 'control.condition',
    ref: 'system.condition',
    condition: {expression: compileDecisionExpression(decisionCondition)},
  });
  edges.push({
    id: `${beforeDecision}-${decisionNodeId}`,
    source: beforeDecision,
    target: decisionNodeId,
  });

  const urgentOutcomeId = resolveIdentity(
    resolveNodeId,
    decision,
    'urgent-outcome',
    'urgent-outcome',
  );
  const standardOutcomeId = resolveIdentity(
    resolveNodeId,
    decision,
    'standard-outcome',
    'standard-outcome',
  );
  decisionOutcomeIds.set(decision, {urgent: urgentOutcomeId, standard: standardOutcomeId});

  const appendBranch = (
    branch: {blocks: ConnectGuidedBlock[]; outcome: string},
    name: 'urgent' | 'standard',
    handle: 'true' | 'false',
  ): void => {
    const firstEdge = {source_handle: handle, label: handle};
    let previousId = decisionNodeId;
    if (branch.blocks.length > 0) {
      previousId = appendActions(
        branch.blocks as Array<ConnectGuidedActionBlock | ConnectGuidedForEachBlock>,
        `${name}-`,
        previousId,
        firstEdge,
      );
    }
    const outcomeId = name === 'urgent' ? urgentOutcomeId : standardOutcomeId;
    nodes.push({
      id: outcomeId,
      type: 'control.transform',
      ref: 'system.transform',
      input: {
        outcome: requiredText(branch.outcome, `${capitalize(name)} outcome`),
        subject: '$.manual.subject',
        details: '$.manual.details',
        route: name,
      },
    });
    edges.push({
      id: `${previousId}-${outcomeId}`,
      source: previousId,
      target: outcomeId,
      ...(branch.blocks.length === 0 ? firstEdge : {}),
    });
  };

  appendBranch(decision.urgent, 'urgent', 'true');
  appendBranch(decision.standard, 'standard', 'false');

  assertUniqueNodeIds(nodes);
  assertPriorStepBindings(draft, blockNodeIds, catalog);
  return {
    workflow: buildWorkflow(draft.name, nodes, edges, connections),
    triggerNodeId,
    blockNodeIds,
    decisionOutcomeIds,
  };
}

export function projectGuidedWorkflow(workflow: ConnectWorkflow): ConnectGuidedWorkflowDraft | null {
  const outline = projectGuidedWorkflowOutline(workflow);
  if (!outline) return null;
  const decision = outline.blocks[outline.blocks.length - 1];
  if (decision?.kind !== 'priority-decision'
    || decision.urgent.blocks.length !== 0
    || decision.standard.blocks.length !== 0) return null;
  if (canonicalJson(normalizeDecisionCondition(decision.condition))
    !== canonicalJson(DEFAULT_DECISION_CONDITION)) return null;
  const before = outline.blocks.slice(0, -1);
  const account = before[0]?.kind === 'verify-local-account' ? 'local-preview' : 'none';
  const expected = account === 'local-preview'
    ? ['verify-local-account', 'prepare-information']
    : ['prepare-information'];
  if (canonicalJson(before.map(block => block.kind)) !== canonicalJson(expected)) return null;
  return {
    name: outline.name,
    urgentOutcome: decision.urgent.outcome,
    standardOutcome: decision.standard.outcome,
    account,
  };
}

export function projectGuidedWorkflowOutline(
  workflow: ConnectWorkflow,
  projectConnector?: ConnectGuidedConnectorProjector,
  catalog?: readonly ConnectWorkflowNodePaletteItem[],
): ConnectGuidedWorkflowOutline | null {
  if (
    !hasOnlyKeys(workflow, [
      'id', 'name', 'enabled', 'version', 'tags', 'nodes', 'edges', 'connections',
      'owner_client_id', 'owner_tenant_id',
    ])
    || workflow.version !== 1
    || workflow['enabled'] !== true
    || typeof workflow.name !== 'string'
    || !Array.isArray(workflow.nodes)
    || !Array.isArray(workflow.edges)
  ) return null;

  const decisionIndex = workflow.nodes.findIndex(node => node.id === 'priority-decision');
  const urgentOutcomeIndex = workflow.nodes.findIndex(node => node.id === 'urgent-outcome');
  const standardOutcomeIndex = workflow.nodes.findIndex(node => node.id === 'standard-outcome');

  const connectionDeclarations = readConnectionDeclarations(workflow['connections']);
  if (connectionDeclarations === null) return null;
  if (decisionIndex === -1) {
    if (urgentOutcomeIndex !== -1 || standardOutcomeIndex !== -1) return null;
    const actions = projectActions(
      workflow.nodes.slice(1), '', projectConnector, connectionDeclarations,
    );
    if (actions === null || actions.length === 0) return null;
    try {
      const draft: ConnectGuidedWorkflowOutline = {name: workflow.name, blocks: actions};
      const expected = compileGuidedWorkflow(draft, catalog);
      return workflowMatchesCompiled(workflow, expected) ? draft : null;
    } catch {
      return null;
    }
  }
  if (decisionIndex < 1 || urgentOutcomeIndex <= decisionIndex
    || standardOutcomeIndex <= urgentOutcomeIndex
    || standardOutcomeIndex !== workflow.nodes.length - 1) return null;
  const before = projectActions(
    workflow.nodes.slice(1, decisionIndex), '', projectConnector, connectionDeclarations,
  );
  const urgent = projectActions(
    workflow.nodes.slice(decisionIndex + 1, urgentOutcomeIndex),
    'urgent-',
    projectConnector,
    connectionDeclarations,
  );
  const standard = projectActions(
    workflow.nodes.slice(urgentOutcomeIndex + 1, standardOutcomeIndex),
    'standard-',
    projectConnector,
    connectionDeclarations,
  );
  const urgentOutcome = readOutcome(workflow.nodes[urgentOutcomeIndex]);
  const standardOutcome = readOutcome(workflow.nodes[standardOutcomeIndex]);
  const condition = readDecisionCondition(workflow.nodes[decisionIndex]);
  if (before === null || urgent === null || standard === null
    || urgentOutcome === null || standardOutcome === null || condition === null) return null;

  try {
    const draft: ConnectGuidedWorkflowOutline = {
      name: workflow.name,
      blocks: [...before, {
        kind: 'priority-decision',
        condition,
        urgent: {blocks: urgent, outcome: urgentOutcome},
        standard: {blocks: standard, outcome: standardOutcome},
      }],
    };
    const expected = compileGuidedWorkflow(draft, catalog);
    return workflowMatchesCompiled(workflow, expected) ? draft : null;
  } catch {
    return null;
  }
}

function compileAction(
  action: ConnectGuidedActionBlock | ConnectGuidedForEachBlock,
  prefix: string,
  nestedInLoop: boolean,
  occurrence = 1,
  resolveNodeId?: ConnectGuidedNodeIdentityResolver,
  blockNodeIds = new Map<ConnectGuidedBlock, string>(),
): CompiledAction {
  const occurrenceSuffix = occurrence > 1 ? `-${occurrence}` : '';
  if (action.kind === 'prepare-information') {
    const id = resolveIdentity(
      resolveNodeId,
      action,
      'action',
      `${prefix}prepare-information${occurrenceSuffix}`,
    );
    blockNodeIds.set(action, id);
    return {
      entryId: id,
      exitId: id,
      nodes: [{
        id,
        type: 'control.transform',
        ref: 'system.transform',
        input: nestedInLoop ? {
          subject: '{{item.subject}}',
          details: '{{item.details}}',
          priority: '{{item.priority}}',
        } : {
          subject: '$.manual.subject',
          details: '$.manual.details',
          priority: '$.manual.priority',
        },
      }],
      edges: [],
      connections: [],
    };
  }
  if (action.kind === 'verify-local-account') {
    const id = resolveIdentity(
      resolveNodeId,
      action,
      'action',
      `${prefix}verify-local-account${occurrenceSuffix}`,
    );
    blockNodeIds.set(action, id);
    return {
      entryId: id,
      exitId: id,
      nodes: [{
        id,
        type: 'api.connector',
        ref: 'local.preview.verify',
        connection: 'local_preview',
        input: {probe: 'ready'},
      }],
      edges: [],
      connections: [{name: 'local_preview', ref: 'local.preview'}],
    };
  }
  if (action.kind === 'connector-operation') {
    const id = resolveIdentity(
      resolveNodeId,
      action,
      'action',
      `${prefix}${operationId(action.operationRef)}${occurrenceSuffix}`,
    );
    blockNodeIds.set(action, id);
    return {
      entryId: id,
      exitId: id,
      nodes: [{
        id,
        type: 'api.connector',
        ref: action.operationRef,
        connection: action.connectionSlot,
        input: cloneJson(action.input),
      }],
      edges: [],
      connections: [{name: action.connectionSlot, ref: action.connectionProviderRef}],
    };
  }

  const id = resolveIdentity(
    resolveNodeId,
    action,
    'action',
    `${prefix}for-each-item${occurrenceSuffix}`,
  );
  blockNodeIds.set(action, id);
  const body = compileAction(
    action.body[0] as ConnectGuidedActionBlock,
    `${id}-`,
    true,
    1,
    resolveNodeId,
    blockNodeIds,
  );
  return {
    entryId: id,
    exitId: body.exitId,
    nodes: [{
      id,
      type: 'control.loop',
      ref: 'system.loop',
      loop: {collection: '$.ctx.manual.items', item_alias: 'item', max_items: 10},
    }, ...body.nodes],
    edges: [{id: `${id}-${body.entryId}`, source: id, target: body.entryId}],
    connections: body.connections,
  };
}

function actionOccurrenceKey(action: ConnectGuidedActionBlock | ConnectGuidedForEachBlock): string {
  return action.kind === 'connector-operation'
    ? `${action.kind}:${action.operationRef}`
    : action.kind;
}

function resolveIdentity(
  resolver: ConnectGuidedNodeIdentityResolver | undefined,
  block: ConnectGuidedBlock | null,
  role: ConnectGuidedNodeRole,
  suggestedId: string,
): string {
  const resolved = resolver?.(block, role, suggestedId) ?? suggestedId;
  if (typeof resolved !== 'string' || resolved.trim().length === 0) {
    throw new Error('Workflow node identity is invalid.');
  }
  return resolved;
}

function assertUniqueNodeIds(nodes: ConnectWorkflowNode[]): void {
  const ids = new Set<string>();
  nodes.forEach(node => {
    if (ids.has(node.id)) throw new Error('Workflow node identity is ambiguous.');
    ids.add(node.id);
  });
}

function projectActions(
  nodes: ConnectWorkflowNode[],
  prefix: string,
  projectConnector: ConnectGuidedConnectorProjector | undefined,
  connectionDeclarations: Map<string, string>,
): ConnectGuidedBlock[] | null {
  const actions: ConnectGuidedBlock[] = [];
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    if (node.type === 'control.transform' && node.ref === 'system.transform'
      && isPrepareInput(node['input'], prefix.includes('for-each-item'))) {
      actions.push({kind: 'prepare-information'});
    } else if (node.type === 'api.connector' && projectConnector) {
      const slot = typeof node['connection'] === 'string' ? node['connection'] : '';
      const providerRef = connectionDeclarations.get(slot);
      const block = providerRef ? projectConnector(node, providerRef) : null;
      if (block && block.connectionSlot === slot && block.connectionProviderRef === providerRef) {
        actions.push(block);
      } else if (node.ref === 'local.preview.verify'
        && slot === 'local_preview'
        && canonicalJson(node['input']) === canonicalJson({probe: 'ready'})) {
        actions.push({kind: 'verify-local-account'});
      } else return null;
    } else if (node.type === 'api.connector' && node.ref === 'local.preview.verify'
      && node['connection'] === 'local_preview'
      && canonicalJson(node['input']) === canonicalJson({probe: 'ready'})) {
      actions.push({kind: 'verify-local-account'});
    } else if (node.type === 'control.loop' && node.ref === 'system.loop'
      && canonicalJson(node['loop']) === canonicalJson({
        collection: '$.ctx.manual.items', item_alias: 'item', max_items: 10,
      })) {
      const bodyNode = nodes[index + 1];
      const body = bodyNode
        ? projectActions([bodyNode], `${node.id}-`, projectConnector, connectionDeclarations)
        : null;
      if (body === null || body.length !== 1 || body[0].kind === 'for-each-item'
        || body[0].kind === 'priority-decision') return null;
      actions.push({kind: 'for-each-item', body});
      index += 1;
    } else return null;
  }
  return actions;
}

function validateDraft(draft: ConnectGuidedWorkflowOutline): void {
  if (!isRecord(draft) || !hasOnlyKeys(draft, ['name', 'blocks'])) {
    throw new Error('Workflow contains unsupported technical properties.');
  }
  requiredText(draft.name, 'Workflow name');
  if (!Array.isArray(draft.blocks) || draft.blocks.length < 1
    || draft.blocks.length > MAX_BLOCKS_PER_SECTION + 1) {
    throw new Error('Workflow blocks are invalid.');
  }
  const decisions = draft.blocks.filter(block => block?.kind === 'priority-decision');
  if (decisions.length > 1 || decisions.length === 1
    && draft.blocks[draft.blocks.length - 1] !== decisions[0]) {
    throw new Error('Workflow may contain one final decision.');
  }
  const decision = finalDecision(draft.blocks);
  const actions = decision ? draft.blocks.slice(0, -1) : draft.blocks;
  if (actions.length === 0 && !decision) throw new Error('Workflow requires at least one action.');
  validateActions(actions, 'Workflow blocks');
  if (decision) validateDecision(decision);
}

function upgradeLegacyDraft(draft: ConnectGuidedWorkflowDraft): ConnectGuidedWorkflowOutline {
  if (!isRecord(draft)
    || !hasOnlyKeys(draft, ['name', 'urgentOutcome', 'standardOutcome', 'account'])) {
    throw new Error('Workflow contains unsupported technical properties.');
  }
  const account = draft.account ?? 'none';
  if (account !== 'none' && account !== 'local-preview') {
    throw new Error('Account choice is invalid.');
  }
  return {
    name: draft.name,
    blocks: [
      ...(account === 'local-preview'
        ? [{kind: 'verify-local-account'} as ConnectGuidedActionBlock]
        : []),
      {kind: 'prepare-information'},
      {
        kind: 'priority-decision',
        condition: {...DEFAULT_DECISION_CONDITION},
        urgent: {blocks: [], outcome: draft.urgentOutcome},
        standard: {blocks: [], outcome: draft.standardOutcome},
      },
    ],
  };
}

function isOutline(
  value: ConnectGuidedWorkflowDraft | ConnectGuidedWorkflowOutline,
): value is ConnectGuidedWorkflowOutline {
  return 'blocks' in value;
}

function validateDecision(decision: ConnectGuidedPriorityDecisionBlock): void {
  if (!isRecord(decision) || !hasOnlyKeys(decision, ['kind', 'condition', 'urgent', 'standard'])
    || decision.kind !== 'priority-decision') throw new Error('Priority decision is invalid.');
  normalizeDecisionCondition(decision.condition);
  validateBranch(decision.urgent, 'Urgent');
  validateBranch(decision.standard, 'Standard');
}

function validateBranch(branch: {blocks: ConnectGuidedBlock[]; outcome: string}, label: string): void {
  if (!isRecord(branch) || !hasOnlyKeys(branch, ['blocks', 'outcome'])) {
    throw new Error(`${label} branch contains unsupported technical properties.`);
  }
  validateActions(branch.blocks, `${label} branch blocks`);
  requiredText(branch.outcome, `${label} outcome`);
}

function validateActions(blocks: ConnectGuidedBlock[], label: string, nestedInLoop = false): void {
  if (!Array.isArray(blocks) || blocks.length > MAX_BLOCKS_PER_SECTION) {
    throw new Error(`${label} is invalid.`);
  }
  blocks.forEach(block => {
    if (!isRecord(block) || typeof block.kind !== 'string') {
      throw new Error(`${label} contains an invalid block.`);
    }
    if (block.kind === 'prepare-information' || block.kind === 'verify-local-account') {
      if (!hasOnlyKeys(block, ['kind'])) {
        throw new Error(`${label} contains unsupported technical properties.`);
      }
      return;
    }
    if (block.kind === 'connector-operation') {
      if (!hasOnlyKeys(block, [
        'kind', 'operationRef', 'connectionSlot', 'connectionProviderRef', 'input',
      ])
        || !isOperationRef(block.operationRef)
        || !isConnectionName(block.connectionSlot)
        || !isProviderRef(block.connectionProviderRef)
        || !isRecord(block.input)
        || !isJsonValue(block.input)
        || !hasSupportedGuidedBindings(block.input, nestedInLoop)) {
        throw new Error(`${label} contains an invalid connector operation.`);
      }
      return;
    }
    if (block.kind !== 'for-each-item' || !hasOnlyKeys(block, ['kind', 'body'])) {
      throw new Error(`${label} contains an unknown block.`);
    }
    if (!Array.isArray(block.body) || block.body.length !== 1
      || block.body[0]?.kind === 'for-each-item'
      || block.body[0]?.kind === 'priority-decision') {
      throw new Error('For-each currently requires exactly one action in its body.');
    }
    validateActions(block.body, 'For-each body', true);
  });
}

function hasSupportedGuidedBindings(value: unknown, nestedInLoop: boolean): boolean {
  if (typeof value === 'string') {
    if (!isConnectGuidedRuntimeLikeString(value)) return true;
    return isConnectGuidedBindingToken(value)
      && (nestedInLoop || !isConnectGuidedCurrentItemBindingToken(value));
  }
  if (Array.isArray(value)) return value.every(item => hasSupportedGuidedBindings(item, nestedInLoop));
  return !isRecord(value) || Object.values(value).every(item => hasSupportedGuidedBindings(item, nestedInLoop));
}

function assertPriorStepBindings(
  outline: ConnectGuidedWorkflowOutline,
  blockNodeIds: Map<ConnectGuidedBlock, string>,
  catalog: readonly ConnectWorkflowNodePaletteItem[] | undefined,
): void {
  const containsPriorBinding = (value: unknown): boolean => {
    if (parseConnectGuidedPriorStepBindingToken(value)) return true;
    if (Array.isArray(value)) return value.some(containsPriorBinding);
    return isRecord(value) && Object.values(value).some(containsPriorBinding);
  };
  if (!outline.blocks.some(block => blockContainsPriorBinding(block, containsPriorBinding))) return;
  if (!catalog) throw new Error('Prior-step output authority is unavailable.');

  const decision = finalDecision(outline.blocks);
  const topLevel = decision ? outline.blocks.slice(0, -1) : outline.blocks;
  validateBindingContainer(topLevel, [], blockNodeIds, catalog, false);
  if (!decision) return;
  const topSources = topLevel.filter(isPriorOutputProducer);
  validateBindingContainer(decision.urgent.blocks, topSources, blockNodeIds, catalog, false);
  validateBindingContainer(decision.standard.blocks, topSources, blockNodeIds, catalog, false);
}

function finalDecision(blocks: ConnectGuidedBlock[]): ConnectGuidedPriorityDecisionBlock | null {
  const candidate = blocks[blocks.length - 1];
  return candidate?.kind === 'priority-decision' ? candidate : null;
}

function buildWorkflow(
  name: string,
  nodes: ConnectWorkflowNode[],
  edges: ConnectWorkflowEdge[],
  connections: Map<string, string>,
): ConnectWorkflow {
  return {
    name: requiredText(name, 'Workflow name'),
    enabled: true,
    version: 1,
    tags: [...(connections.size > 0 ? LOCAL_PREVIEW_TAGS : PROVIDER_FREE_TAGS)],
    nodes,
    edges,
    ...(connections.size > 0 ? {
      connections: [...connections].map(([connectionName, ref]) => ({name: connectionName, ref})),
    } : {}),
  };
}

function workflowMatchesCompiled(workflow: ConnectWorkflow, expected: ConnectWorkflow): boolean {
  return workflow.name === expected.name
    && canonicalJson(workflow.tags) === canonicalJson(expected.tags)
    && canonicalJson(workflow.nodes) === canonicalJson(expected.nodes)
    && canonicalJson(workflow.edges) === canonicalJson(expected.edges)
    && canonicalJson(workflow['connections']) === canonicalJson(expected['connections']);
}

function blockContainsPriorBinding(
  block: ConnectGuidedBlock,
  containsPriorBinding: (value: unknown) => boolean,
): boolean {
  if (block.kind === 'connector-operation') return containsPriorBinding(block.input);
  if (block.kind === 'for-each-item') return block.body.some(child =>
    blockContainsPriorBinding(child, containsPriorBinding));
  if (block.kind === 'priority-decision') return [
    ...block.urgent.blocks,
    ...block.standard.blocks,
  ].some(child => blockContainsPriorBinding(child, containsPriorBinding));
  return false;
}

function validateBindingContainer(
  blocks: ConnectGuidedBlock[],
  inheritedSources: ConnectGuidedBlock[],
  blockNodeIds: Map<ConnectGuidedBlock, string>,
  catalog: readonly ConnectWorkflowNodePaletteItem[],
  nestedInLoop: boolean,
): void {
  const priorSources = [...inheritedSources];
  for (const block of blocks) {
    if (block.kind === 'connector-operation') {
      validateConnectorPriorBindings(block, nestedInLoop ? [] : priorSources, blockNodeIds, catalog);
    } else if (block.kind === 'for-each-item') {
      validateBindingContainer(block.body, [], blockNodeIds, catalog, true);
    }
    if (!nestedInLoop && isPriorOutputProducer(block)) priorSources.push(block);
  }
}

function validateConnectorPriorBindings(
  target: ConnectGuidedConnectorOperationBlock,
  sources: ConnectGuidedBlock[],
  blockNodeIds: Map<ConnectGuidedBlock, string>,
  catalog: readonly ConnectWorkflowNodePaletteItem[],
): void {
  const targetDescriptor = resolveConnectNodeOperation(catalog, target.operationRef);
  if (!targetDescriptor) throw new Error('Connector operation authority is unavailable.');
  const bindings = new Map<string, ConnectNodeFieldShape>();
  for (const source of sources) {
    const operationRef = sourceOperationRef(source);
    const nodeId = blockNodeIds.get(source);
    const descriptor = operationRef ? resolveConnectNodeOperation(catalog, operationRef) : null;
    if (!nodeId || !descriptor) continue;
    for (const [outputPath, shape] of Object.entries(descriptor.output)) {
      const token = `$.${nodeId}.output.${outputPath}`;
      if (bindings.has(token)) throw new Error('Prior-step output authority is ambiguous.');
      bindings.set(token, shape);
    }
  }
  const normalized = readConnectNodeOperationInput(
    targetDescriptor.node.presentation!,
    targetDescriptor.operation.action,
    target.input,
    {
      allowGuidedBindings: true,
      validatePriorStepBinding: (binding, targetShape) => {
        const sourceShape = bindings.get(binding);
        return sourceShape !== undefined && isConnectNodeOutputCompatible(sourceShape, targetShape);
      },
    },
  );
  if (!normalized || canonicalJson(normalized) !== canonicalJson(target.input)) {
    throw new Error('Prior-step output binding is unavailable.');
  }
}

function isPriorOutputProducer(block: ConnectGuidedBlock): boolean {
  return block.kind === 'connector-operation' || block.kind === 'verify-local-account';
}

function sourceOperationRef(block: ConnectGuidedBlock): string | null {
  if (block.kind === 'connector-operation') return block.operationRef;
  return block.kind === 'verify-local-account' ? 'local.preview.verify' : null;
}

function isPrepareInput(value: unknown, nestedInLoop: boolean): boolean {
  const expected = nestedInLoop ? {
    subject: '{{item.subject}}',
    details: '{{item.details}}',
    priority: '{{item.priority}}',
  } : {
    subject: '$.manual.subject',
    details: '$.manual.details',
    priority: '$.manual.priority',
  };
  return canonicalJson(value) === canonicalJson(expected);
}

function requiredText(value: string, label: string): string {
  const normalized = typeof value === 'string' ? value.trim() : '';
  if (!normalized) throw new Error(`${label} is required.`);
  return normalized;
}

function normalizeDecisionCondition(
  condition: ConnectGuidedDecisionCondition | undefined,
): ConnectGuidedDecisionCondition {
  if (condition === undefined) return {...DEFAULT_DECISION_CONDITION};
  if (!isRecord(condition) || !hasOnlyKeys(condition, ['field', 'operator', 'value'])
    || !DECISION_FIELDS.includes(condition.field as typeof DECISION_FIELDS[number])
    || !DECISION_OPERATORS.includes(condition.operator as typeof DECISION_OPERATORS[number])) {
    throw new Error('Decision condition is invalid.');
  }
  const value = requiredText(condition.value, 'Decision condition value');
  if (value.length > MAX_CONDITION_VALUE_LENGTH) {
    throw new Error(`Decision condition value must be ${MAX_CONDITION_VALUE_LENGTH} characters or fewer.`);
  }
  if (JSON.stringify(value).slice(1, -1) !== value) {
    throw new Error(
      'Decision condition value must use plain text without quotation marks, backslashes, or line breaks.',
    );
  }
  return {
    field: condition.field as ConnectGuidedDecisionCondition['field'],
    operator: condition.operator as ConnectGuidedDecisionCondition['operator'],
    value,
  };
}

function compileDecisionExpression(condition: ConnectGuidedDecisionCondition): string {
  const operator = condition.operator === 'equals' ? '===' : '!==';
  return `$.manual.${condition.field} ${operator} ${JSON.stringify(condition.value)}`;
}

function readDecisionCondition(
  node: ConnectWorkflowNode | undefined,
): ConnectGuidedDecisionCondition | null {
  const condition = node?.['condition'];
  if (!isRecord(condition) || typeof condition['expression'] !== 'string') return null;
  const expression = condition['expression'];
  const match = /^\$\.manual\.(priority|subject|details) (===|!==) ("(?:[^"\\]|\\.)*")$/
    .exec(expression);
  if (!match) return null;
  let value: unknown;
  try {
    value = JSON.parse(match[3]);
  } catch {
    return null;
  }
  if (typeof value !== 'string') return null;
  try {
    const parsed = normalizeDecisionCondition({
      field: match[1] as ConnectGuidedDecisionCondition['field'],
      operator: match[2] === '===' ? 'equals' : 'not-equals',
      value,
    });
    return compileDecisionExpression(parsed) === expression ? parsed : null;
  } catch {
    return null;
  }
}

function readOutcome(node: ConnectWorkflowNode | undefined): string | null {
  const input = node?.['input'];
  return isRecord(input) && typeof input['outcome'] === 'string' ? input['outcome'] : null;
}

function hasOnlyKeys(value: Record<string, unknown>, allowed: string[]): boolean {
  const keys = new Set(allowed);
  return Object.keys(value).every(key => keys.has(key));
}

function readConnectionDeclarations(value: unknown): Map<string, string> | null {
  const result = new Map<string, string>();
  if (value === undefined) return result;
  if (!Array.isArray(value)) return null;
  for (const candidate of value) {
    if (!isRecord(candidate) || !hasOnlyKeys(candidate, ['name', 'ref'])
      || !isConnectionName(candidate['name']) || !isProviderRef(candidate['ref'])
      || result.has(candidate['name'])) return null;
    result.set(candidate['name'], candidate['ref']);
  }
  return result;
}

function isOperationRef(value: unknown): value is string {
  return typeof value === 'string'
    && /^[a-z][a-z0-9_-]*(?:\.[a-z][a-z0-9_-]*){2,}$/.test(value);
}

function isConnectionName(value: unknown): value is string {
  return typeof value === 'string'
    && value === value.trim()
    && value.length > 0
    && value.length <= 120
    && ![...value].some(character => {
      const code = character.charCodeAt(0);
      return code < 32 || code === 127;
    });
}

function isProviderRef(value: unknown): value is string {
  return typeof value === 'string'
    && /^[a-z][a-z0-9_-]*(?:\.[a-z][a-z0-9_-]*)+$/.test(value);
}

function operationId(operationRef: string): string {
  return `module-${operationRef.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;
}

function isJsonValue(value: unknown): boolean {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.every(item => isJsonValue(item));
  return isRecord(value) && Object.values(value).every(item => isJsonValue(item));
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function canonicalJson(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(item => canonicalize(item));
  if (!isRecord(value)) return value;
  return Object.fromEntries(Object.keys(value).sort().map(key => [key, canonicalize(value[key])]));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
